import { useEffect } from 'react';

export interface ServiceItem {
  icon: string;
  title: string;
  description: string;
  detail: string;
}

interface ServiceModalProps {
  service: ServiceItem | null;
  onClose: () => void;
  ctaLabel: string;
  onCtaClick: () => void;
}

export default function ServiceModal({ service, onClose, ctaLabel, onCtaClick }: ServiceModalProps) {
  useEffect(() => {
    if (!service) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [service, onClose]);

  if (!service) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-gray-900 border border-gray-700 rounded-2xl max-w-lg w-full p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'modalIn 0.25s ease' }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        {/* Icon */}
        <div className="w-14 h-14 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center mb-5">
          <i className={`${service.icon} text-2xl text-black`}></i>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>

        {/* Short desc */}
        <p className="text-yellow-400/80 text-sm font-medium mb-4">{service.description}</p>

        {/* Divider */}
        <div className="w-12 h-0.5 bg-yellow-400/40 mb-4"></div>

        {/* Detail */}
        <p className="text-gray-300 text-sm leading-relaxed mb-7">{service.detail}</p>

        {/* CTA */}
        <button
          onClick={() => { onCtaClick(); onClose(); }}
          className="w-full py-3 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-all duration-300 hover:scale-[1.02] cursor-pointer flex items-center justify-center gap-2 text-sm whitespace-nowrap"
        >
          <i className="ri-send-plane-line"></i>
          {ctaLabel}
        </button>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.93) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
